// Reroute `/assets/*` URLs to an external origin so the browser can pull
// the ~557 MB public/assets/ tree from a static server running alongside
// it instead of from this checkout's Vite dev server.
//
// Configure with VITE_ASSETS_BASE_URL (in .env.local, or inline on the
// dev command, e.g. `VITE_ASSETS_BASE_URL=http://localhost:8000 npm run dev`).
// Empty/unset = no rewrite (default — files are served from public/).
//
// CORS requirement: the external host MUST send
// `Access-Control-Allow-Origin: *` (or echo this app's origin), because
// `fetch()` and THREE's loaders go through CORS.
// `python3 -m http.server` does NOT add this header. Use one of:
//   npx http-server -p 8000 --cors
//   npx serve -p 8000 --cors
//   any nginx/caddy with `add_header Access-Control-Allow-Origin "*";`

import * as THREE from 'three'

const RAW_BASE = import.meta.env.VITE_ASSETS_BASE_URL || ''
export const ASSETS_BASE_URL = RAW_BASE.replace(/\/+$/, '')

const ASSETS_PREFIX = '/assets/'

export function assetUrl(path) {
  if (!ASSETS_BASE_URL || typeof path !== 'string') return path
  if (path.startsWith(ASSETS_PREFIX)) return ASSETS_BASE_URL + path
  return path
}

function rewrite(url) {
  if (typeof url !== 'string' || !ASSETS_BASE_URL) return url
  if (url.startsWith(ASSETS_PREFIX)) return ASSETS_BASE_URL + url
  if (typeof window !== 'undefined') {
    const origin = window.location.origin
    if (url.startsWith(origin + ASSETS_PREFIX)) {
      return ASSETS_BASE_URL + url.slice(origin.length)
    }
  }
  return url
}

if (ASSETS_BASE_URL && typeof window !== 'undefined') {
  // 1. fetch — JSON manifests, ARKit face JSONs, hair sidecar bins, etc.
  const origFetch = window.fetch.bind(window)
  window.fetch = (input, init) => {
    if (typeof input === 'string') return origFetch(rewrite(input), init)
    if (input && typeof input === 'object' && 'url' in input) {
      const next = rewrite(input.url)
      if (next !== input.url) return origFetch(new Request(next, input), init)
    }
    return origFetch(input, init)
  }

  // 2. THREE loaders — GLTFLoader, FBXLoader, PLYLoader, TextureLoader all
  //    use DefaultLoadingManager unless explicitly given another. None of
  //    this codebase passes a custom manager, so this catches all of them.
  THREE.DefaultLoadingManager.setURLModifier(rewrite)

  // 3. Audio — `new Audio(url)` does not go through fetch. EventRuntime's
  //    playAudio node and several preview buttons use it directly.
  const OrigAudio = window.Audio
  function PatchedAudio(url, ...rest) {
    return url !== undefined ? new OrigAudio(rewrite(url), ...rest) : new OrigAudio()
  }
  PatchedAudio.prototype = OrigAudio.prototype
  window.Audio = PatchedAudio

  console.info(
    `%c[assetUrl]%c /assets/* → ${ASSETS_BASE_URL}`,
    'color:#f80;font-weight:bold', 'color:inherit',
  )
}
